import { useCallback, useEffect, useRef } from 'react';
import {
  KeyboardEvent,
  MouseEvent,
  WheelEvent,
  TouchEvent,
  FocusEvent,
  FormEvent,
  ClipboardEvent,
  SelectionEvent,
  ViewportEvent,
  CompositionEvent,
  DeviceOrientationEvent,
  GlobalErrorEventLog,
  KeyboardLog,
  MouseLog,
  WheelLog,
  TouchLog,
  FocusLog,
  FormLog,
  ClipboardLog,
  SelectionLog,
  ViewportLog,
  CompositionLog,
  DeviceOrientationLog,
  ErrorLog,
} from './eventLogs';
import { ElementLog, ElementEntry } from './compactEventFormat';

// Add WebGL debug renderer info type
interface WEBGL_debug_renderer_info {
  UNMASKED_VENDOR_WEBGL: number;
  UNMASKED_RENDERER_WEBGL: number;
}

interface InputLoggerData {
  session_metadata: {
    session_start_time: number;
    user_agent: string;
    viewport: {
      width: number;
      height: number;
    };
    device_type: 'desktop' | 'mobile' | 'tablet';
    screen_resolution: string;
    color_depth: number;
    pixel_ratio: number;
    timezone_offset: number;
    // Enhanced browser fingerprinting
    webgl_vendor?: string;
    webgl_renderer?: string;
    canvas_fingerprint?: string;
    memory_usage?: number;
    plugins_count?: number;
    fonts_available?: number;
    // UA Client Hints (Chromium-based browsers)
    ua_ch?: {
      mobile?: boolean;
      brands?: Array<{ brand: string; version: string }>;
      architecture?: string;
      bitness?: string;
      model?: string;
      platform?: string;
      platform_version?: string;
      ua_full_version?: string;
      full_version_list?: Array<{ brand: string; version: string }>;
    };
    // Hardware
    hardware_concurrency?: number;
    device_memory_gb?: number;
    max_touch_points?: number;
    // Automation detection
    automation_flags: {
      webdriver: boolean;
      phantom: boolean;
      selenium_vars: number;
    };
    // Mobile-specific
    battery_level?: number;
    connection_type?: string;
    connection_speed?: number;
    // Permissions and devices
    permissions?: {
      [name: string]: 'granted' | 'denied' | 'prompt' | 'unknown';
    };
    media_devices?: {
      audioinput?: number;
      videoinput?: number;
      audiooutput?: number;
      other?: number;
      total?: number;
    };
    // Session continuity
    referrer?: string;
    utm_source?: string;
    utm_campaign?: string;
    session_start_url?: string;
    local_storage_keys?: number;
    cookies_count?: number;
    return_user?: boolean;
  };
  // Element log array (contains all unique element descriptions and bounds)
  elements: ElementEntry[];
  keyboard_events: KeyboardEvent[];
  mouse_events: MouseEvent[];
  wheel_events: WheelEvent[];
  touch_events: TouchEvent[];
  focus_events: FocusEvent[];
  form_events: FormEvent[];
  clipboard_events: ClipboardEvent[];
  selection_events: SelectionEvent[];
  viewport_events: ViewportEvent[];
  composition_events: CompositionEvent[];
  device_orientation_events: DeviceOrientationEvent[];
  error_events: GlobalErrorEventLog[];
}

interface UseInputLoggerOptions {
  enabled?: boolean;
  maxEvents?: number;
  useCompactFormat?: boolean;
}

/**
 * Helper function to convert performance.now() to microseconds as an integer
 * performance.now() returns milliseconds with sub-millisecond precision
 * This function converts to microseconds for more precise timing
 */
export const getTimestampMicroseconds = (): number => {
  return Math.round(performance.now() * 1000);
};

/**
 * Custom hook for logging keyboard input events for fraud detection
 * Captures timing patterns and interaction metadata without storing actual content
 */
export const useInputLogger = (options: UseInputLoggerOptions = {}) => {
  const {
    enabled = true,
    maxEvents = 10000,
    useCompactFormat = true,
  } = options;

  const sessionStartTime = useRef<number>(getTimestampMicroseconds());

  // Shared element log for all event types
  const elementLog = useRef<ElementLog>(new ElementLog());

  const keyboardEvents = useRef<KeyboardLog>(
    new KeyboardLog(maxEvents, useCompactFormat)
  );
  const mouseEvents = useRef<MouseLog>(
    new MouseLog(maxEvents, useCompactFormat)
  );
  const wheelEvents = useRef<WheelLog>(
    new WheelLog(maxEvents, useCompactFormat)
  );
  const touchEvents = useRef<TouchLog>(
    new TouchLog(maxEvents, useCompactFormat)
  );
  const focusEvents = useRef<FocusLog>(
    new FocusLog(maxEvents, useCompactFormat)
  );
  const formEvents = useRef<FormLog>(new FormLog(maxEvents, useCompactFormat));
  const clipboardEvents = useRef<ClipboardLog>(
    new ClipboardLog(maxEvents, useCompactFormat)
  );
  const selectionEvents = useRef<SelectionLog>(
    new SelectionLog(maxEvents, useCompactFormat)
  );
  const viewportEvents = useRef<ViewportLog>(
    new ViewportLog(maxEvents, useCompactFormat)
  );
  const compositionEvents = useRef<CompositionLog>(
    new CompositionLog(maxEvents, useCompactFormat)
  );
  const deviceOrientationEvents = useRef<DeviceOrientationLog>(
    new DeviceOrientationLog(maxEvents, useCompactFormat)
  );
  const errorEvents = useRef<ErrorLog>(
    new ErrorLog(maxEvents, useCompactFormat)
  );
  const isLogging = useRef<boolean>(false);

  // Helper function to detect device type
  const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/tablet|ipad|playbook|silk/.test(userAgent)) {
      return 'tablet';
    }
    if (
      /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/.test(
        userAgent
      )
    ) {
      return 'mobile';
    }
    return 'desktop';
  };

  // Helper function to detect automation indicators
  const getAutomationFlags = () => {
    const win = window as any;
    return {
      webdriver: navigator.webdriver === true,
      phantom: !!win.callPhantom || !!win.phantom,
      selenium_vars: [
        'webdriver',
        'selenium',
        'driver',
        '__webdriver_script_fn',
        '__selenium_unwrapped',
        '__webdriver_unwrapped',
      ].filter((key) => key in win).length,
    };
  };

  // Helper function to get WebGL info safely
  const getWebGLInfo = () => {
    try {
      const canvas = document.createElement('canvas');
      let gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
      if (!gl) {
        gl = canvas.getContext(
          'experimental-webgl'
        ) as WebGLRenderingContext | null;
      }
      if (!gl) return {};

      const debugInfo = gl.getExtension(
        'WEBGL_debug_renderer_info'
      ) as WEBGL_debug_renderer_info | null;
      return {
        vendor: debugInfo
          ? (gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string)
          : undefined,
        renderer: debugInfo
          ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
          : undefined,
      };
    } catch {
      return {};
    }
  };

  // Helper function to generate canvas fingerprint
  const getCanvasFingerprint = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return undefined;

      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Canvas fingerprint test 🔒', 2, 2);

      return canvas.toDataURL().slice(-50); // Last 50 chars as fingerprint
    } catch {
      return undefined;
    }
  };

  // Helper function to get memory info safely
  const getMemoryInfo = () => {
    try {
      const mem = (performance as any).memory;
      return mem ? mem.usedJSHeapSize : undefined;
    } catch {
      return undefined;
    }
  };

  // Helper function to get connection info
  const getConnectionInfo = () => {
    try {
      const conn =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection;
      return conn
        ? {
            type: conn.effectiveType || conn.type,
            speed: conn.downlink,
          }
        : {};
    } catch {
      return {};
    }
  };

  // Helper to get UA-CH (User-Agent Client Hints)
  const getUAClientHints = async () => {
    try {
      const uaData = (navigator as any).userAgentData;
      if (!uaData) return {};
      const hi = await uaData.getHighEntropyValues?.([
        'architecture',
        'bitness',
        'model',
        'platform',
        'platformVersion',
        'uaFullVersion',
        'fullVersionList',
      ]);
      return {
        mobile: !!uaData.mobile,
        brands: uaData.brands || uaData.getBrands?.() || [],
        architecture: hi?.architecture,
        bitness: hi?.bitness,
        model: hi?.model,
        platform: hi?.platform,
        platform_version: hi?.platformVersion,
        ua_full_version: hi?.uaFullVersion,
        full_version_list: hi?.fullVersionList,
      };
    } catch {
      return {};
    }
  };

  // Helper to get basic hardware info
  const getHardwareInfo = () => {
    try {
      const { deviceMemory } = navigator as any;
      return {
        hardware_concurrency: navigator.hardwareConcurrency || undefined,
        device_memory_gb:
          typeof deviceMemory === 'number' ? deviceMemory : undefined,
        max_touch_points: (navigator as any).maxTouchPoints || 0,
      };
    } catch {
      return {} as any;
    }
  };

  // Helper to get permissions matrix
  const getPermissionsStatus = async () => {
    const result: {
      [name: string]: 'granted' | 'denied' | 'prompt' | 'unknown';
    } = {};
    try {
      if (
        !('permissions' in navigator) ||
        !(navigator as any).permissions?.query
      ) {
        return result;
      }
      const names: string[] = [
        'geolocation',
        'notifications',
        'camera',
        'microphone',
        'clipboard-read',
        'clipboard-write',
        'background-sync',
        'midi',
        'push',
        'persistent-storage',
        'accelerometer',
        'gyroscope',
        'magnetometer',
        'nfc',
        'bluetooth',
        'display-capture',
        'storage-access',
      ];
      await Promise.all(
        names.map(async (name) => {
          try {
            const status = await (navigator as any).permissions.query({
              name,
            } as any);
            result[name] = (status?.state as any) || 'unknown';
          } catch {
            result[name] = 'unknown';
          }
        })
      );
      return result;
    } catch {
      return result;
    }
  };

  // Helper to count media devices
  const getMediaDevicesCounts = async () => {
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices?.();
      if (!devices) return {};
      const counts = devices.reduce(
        (acc, d) => {
          if (d.kind === 'audioinput') acc.audioinput += 1;
          else if (d.kind === 'videoinput') acc.videoinput += 1;
          else if (d.kind === 'audiooutput') acc.audiooutput += 1;
          else acc.other += 1;
          acc.total += 1;
          return acc;
        },
        { audioinput: 0, videoinput: 0, audiooutput: 0, other: 0, total: 0 }
      );
      return counts;
    } catch {
      return {};
    }
  };

  // Helper function to get battery info
  const getBatteryInfo = async () => {
    try {
      const battery = await (navigator as any).getBattery?.();
      return battery
        ? {
            level: battery.level,
            charging: battery.charging,
          }
        : {};
    } catch {
      return {};
    }
  };

  // Helper function to get URL parameters
  const getUrlParameters = () => {
    try {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      return {
        referrer: document.referrer || undefined,
        utm_source: params.get('utm_source') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        session_start_url: window.location.pathname,
      };
    } catch {
      return {};
    }
  };

  // Helper function to get storage info
  const getStorageInfo = () => {
    try {
      return {
        local_storage_keys: localStorage.length,
        cookies_count: document.cookie.split(';').filter((c) => c.trim())
          .length,
        return_user:
          localStorage.length > 0 || document.cookie.includes('session'),
      };
    } catch {
      return {
        local_storage_keys: 0,
        cookies_count: 0,
        return_user: false,
      };
    }
  };

  // Helper function to get active modifiers
  const getActiveModifiers = (event: globalThis.KeyboardEvent): string[] => {
    const modifiers: string[] = [];
    if (event.ctrlKey) modifiers.push('ctrl');
    if (event.shiftKey) modifiers.push('shift');
    if (event.altKey) modifiers.push('alt');
    if (event.metaKey) modifiers.push('meta');
    return modifiers;
  };

  const withTimeout = async <T>(
    promise: Promise<T>,
    timeoutMs: number,
    fallback: T
  ): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<T>((resolve) => {
      timeoutId = setTimeout(() => resolve(fallback), timeoutMs);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };

  const handleKeyboardEvent = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (!isLogging.current || !enabled) return;

      // Skip logging for sensitive keys to avoid capturing passwords/sensitive data
      const sensitiveKeys = [
        'Escape',
        'F1',
        'F2',
        'F3',
        'F4',
        'F5',
        'F6',
        'F7',
        'F8',
        'F9',
        'F10',
        'F11',
        'F12',
      ];
      if (sensitiveKeys.includes(event.key)) return;

      const now = getTimestampMicroseconds();

      const elementIndex = elementLog.current.update(event.target);

      const keyboardEvent: KeyboardEvent = {
        type: event.type as 'keydown' | 'keyup',
        timestamp: now,

        key: event.key,
        code: event.code,
        element_index: elementIndex,
        modifiers: getActiveModifiers(event),
        repeat: event.repeat,
      };

      keyboardEvents.current.update(keyboardEvent);
    },
    [enabled, maxEvents]
  );

  const handleMouseEvent = useCallback(
    (event: globalThis.MouseEvent | globalThis.Event) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const eventType = event.type as
        | 'mousedown'
        | 'mouseup'
        | 'click'
        | 'mousemove'
        | 'scroll'
        | 'contextmenu'
        | 'mouseleave'
        | 'mouseenter';

      // Get coordinates (some events might not have clientX/Y)
      const mouseEvent = event as globalThis.MouseEvent;
      const x = mouseEvent.clientX || 0;
      const y = mouseEvent.clientY || 0;

      const elementIndex = elementLog.current.update(event.target);

      const mouseEventData: MouseEvent = {
        type: eventType,
        timestamp: now,

        x,
        y,
        button: mouseEvent.button,
        element_index: elementIndex,
        // Add scroll position for scroll events
        scroll_top:
          eventType === 'scroll'
            ? (event.target as Element)?.scrollTop || window.scrollY
            : undefined,
        scroll_left:
          eventType === 'scroll'
            ? (event.target as Element)?.scrollLeft || window.scrollX
            : undefined,
      };

      mouseEvents.current.update(mouseEventData);
    },
    [enabled, maxEvents]
  );

  const handleTouchEvent = useCallback(
    (event: globalThis.TouchEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(event.target);

      // Helper to extract touch data with all available properties
      const extractTouchData = (touch: Touch, includeAll = false) => {
        const baseData = {
          identifier: touch.identifier,
          x: touch.clientX,
          y: touch.clientY,
          pressure: touch.force || undefined,
          radiusX: touch.radiusX || undefined,
          radiusY: touch.radiusY || undefined,
          rotationAngle: touch.rotationAngle || undefined,
        };

        if (includeAll) {
          return {
            ...baseData,
            // These are newer properties that may not be available on all devices
            altitudeAngle: (touch as any).altitudeAngle || undefined,
            azimuthAngle: (touch as any).azimuthAngle || undefined,
          };
        }

        return baseData;
      };

      const touches = Array.from(event.touches).map((touch) =>
        extractTouchData(touch, true)
      );
      const changedTouches = Array.from(event.changedTouches).map((touch) =>
        extractTouchData(touch, true)
      );
      const targetTouches = Array.from(event.targetTouches).map((touch) =>
        extractTouchData(touch, true)
      );

      // Get active modifiers (rarely used with touch but can indicate automation)
      const modifiers: string[] = [];
      if (event.ctrlKey) modifiers.push('ctrl');
      if (event.shiftKey) modifiers.push('shift');
      if (event.altKey) modifiers.push('alt');
      if (event.metaKey) modifiers.push('meta');

      const touchEvent: TouchEvent = {
        type: event.type as 'touchstart' | 'touchend' | 'touchmove',
        timestamp: now,

        touches,
        changed_touches: changedTouches,
        target_touches: targetTouches,
        element_index: elementIndex,
        modifiers,
        touch_count: event.touches.length,
      };

      touchEvents.current.update(touchEvent);
    },
    [enabled, maxEvents]
  );

  const handleFocusEvent = useCallback(
    (event: globalThis.FocusEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(event.target);

      const focusEvent: FocusEvent = {
        type: event.type as 'focus' | 'blur',
        timestamp: now,

        element_index: elementIndex,
      };

      focusEvents.current.update(focusEvent);
    },
    [enabled, maxEvents]
  );

  const handleFormEvent = useCallback(
    (event: Event) => {
      if (!isLogging.current || !enabled) return;

      const { target } = event;
      if (
        !target ||
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement
        )
      )
        return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(target);

      const formEvent: FormEvent = {
        type: event.type as 'input' | 'change',
        timestamp: now,
        element_index: elementIndex,
        value_length: target.value ? target.value.length : 0,
      };

      formEvents.current.update(formEvent);
    },
    [enabled, maxEvents]
  );

  const handleWheelEvent = useCallback(
    (event: globalThis.WheelEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(event.target);

      const wheelEvent: WheelEvent = {
        type: 'wheel',
        timestamp: now,

        x: event.clientX,
        y: event.clientY,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        deltaZ: event.deltaZ,
        deltaMode: event.deltaMode,
        element_index: elementIndex,
      };

      wheelEvents.current.update(wheelEvent);
    },
    [enabled, maxEvents]
  );

  const handleClipboardEvent = useCallback(
    (event: globalThis.ClipboardEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(event.target);

      const clipboardEvent: ClipboardEvent = {
        type: event.type as 'copy' | 'paste' | 'cut',
        timestamp: now,

        element_index: elementIndex,
        data_length: event.clipboardData?.getData('text')?.length || 0,
      };

      clipboardEvents.current.update(clipboardEvent);
    },
    [enabled, maxEvents]
  );

  const handleSelectionEvent = useCallback(
    (event: Event) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(event.target);
      const selection = window.getSelection();

      const selectionEvent: SelectionEvent = {
        type: event.type as 'selectstart' | 'selectend' | 'selectionchange',
        timestamp: now,

        element_index: elementIndex,
        selection_length: selection?.toString().length || 0,
        selection_start: (event.target as any)?.selectionStart || undefined,
        selection_end: (event.target as any)?.selectionEnd || undefined,
      };

      selectionEvents.current.update(selectionEvent);
    },
    [enabled, maxEvents]
  );

  const handleWindowErrorEvent = useCallback(
    (event: ErrorEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const log: GlobalErrorEventLog = {
        type: 'error',
        timestamp: now,

        message_length: (event.message || '').length,
      };
      errorEvents.current.update(log);
    },
    [enabled, maxEvents]
  );

  const handleUnhandledRejectionEvent = useCallback(
    (event: PromiseRejectionEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      let messageLen = 0;
      const { reason } = event;
      if (typeof reason === 'string') messageLen = reason.length;
      else if (reason && typeof reason.message === 'string')
        messageLen = reason.message.length;

      const log: GlobalErrorEventLog = {
        type: 'unhandledrejection',
        timestamp: now,

        message_length: messageLen,
      };
      errorEvents.current.update(log);
    },
    [enabled, maxEvents]
  );

  const handleViewportEvent = useCallback(
    (event: Event) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();

      const viewportEvent: ViewportEvent = {
        type: event.type as
          | 'resize'
          | 'visibilitychange'
          | 'focus'
          | 'blur'
          | 'load'
          | 'beforeunload',
        timestamp: now,

        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        visibility_state: document.visibilityState,
        zoom_level: (window as any).devicePixelRatio || 1,
      };

      viewportEvents.current.update(viewportEvent);
    },
    [enabled, maxEvents]
  );

  const handleCompositionEvent = useCallback(
    (event: globalThis.CompositionEvent) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();
      const elementIndex = elementLog.current.update(event.target);

      const compositionEvent: CompositionEvent = {
        type: event.type as
          | 'compositionstart'
          | 'compositionend'
          | 'compositionupdate',
        timestamp: now,

        element_index: elementIndex,
        data_length: event.data ? event.data.length : 0,
      };

      compositionEvents.current.update(compositionEvent);
    },
    [enabled, maxEvents]
  );

  const handleDeviceOrientationEvent = useCallback(
    (event: any) => {
      if (!isLogging.current || !enabled) return;

      const now = getTimestampMicroseconds();

      const deviceEvent: DeviceOrientationEvent = {
        type: event.type as 'deviceorientation' | 'devicemotion',
        timestamp: now,

        alpha: event.alpha,
        beta: event.beta,
        gamma: event.gamma,
        acceleration: event.acceleration
          ? {
              x: event.acceleration.x || 0,
              y: event.acceleration.y || 0,
              z: event.acceleration.z || 0,
            }
          : undefined,
        rotation_rate: event.rotationRate
          ? {
              alpha: event.rotationRate.alpha || 0,
              beta: event.rotationRate.beta || 0,
              gamma: event.rotationRate.gamma || 0,
            }
          : undefined,
      };

      deviceOrientationEvents.current.update(deviceEvent);
    },
    [enabled, maxEvents]
  );

  // Start logging session
  const startLogging = useCallback(() => {
    if (!enabled) return;

    sessionStartTime.current = getTimestampMicroseconds();
    keyboardEvents.current.clear();
    mouseEvents.current.clear();
    wheelEvents.current.clear();
    touchEvents.current.clear();
    focusEvents.current.clear();
    formEvents.current.clear();
    clipboardEvents.current.clear();
    selectionEvents.current.clear();
    viewportEvents.current.clear();
    compositionEvents.current.clear();
    deviceOrientationEvents.current.clear();
    errorEvents.current.clear();
    isLogging.current = true;

    // Add event listeners
    // passive: true tells the browser that the event handler will never call preventDefault(),
    // allowing the browser to optimize scrolling and other default behaviors without waiting
    // for the event handler to complete. This improves performance.

    // Keyboard events
    document.addEventListener('keydown', handleKeyboardEvent, {
      passive: true,
    });
    document.addEventListener('keyup', handleKeyboardEvent, { passive: true });

    // Mouse events
    document.addEventListener('mousedown', handleMouseEvent, { passive: true });
    document.addEventListener('mouseup', handleMouseEvent, { passive: true });
    document.addEventListener('click', handleMouseEvent, { passive: true });
    document.addEventListener('mousemove', handleMouseEvent, { passive: true });
    document.addEventListener('scroll', handleMouseEvent, { passive: true });
    document.addEventListener('contextmenu', handleMouseEvent, {
      passive: true,
    });
    document.addEventListener('mouseleave', handleMouseEvent, {
      passive: true,
    });
    document.addEventListener('mouseenter', handleMouseEvent, {
      passive: true,
    });

    // Wheel events
    document.addEventListener('wheel', handleWheelEvent, { passive: true });

    // Touch events
    document.addEventListener('touchstart', handleTouchEvent, {
      passive: true,
    });
    document.addEventListener('touchend', handleTouchEvent, { passive: true });
    document.addEventListener('touchmove', handleTouchEvent, { passive: true });

    // Focus events (use capture to catch events early)
    document.addEventListener('focus', handleFocusEvent, {
      capture: true,
      passive: true,
    });
    document.addEventListener('blur', handleFocusEvent, {
      capture: true,
      passive: true,
    });

    // Form events
    document.addEventListener('input', handleFormEvent, { passive: true });
    document.addEventListener('change', handleFormEvent, { passive: true });

    // Clipboard events
    document.addEventListener('copy', handleClipboardEvent, { passive: true });
    document.addEventListener('paste', handleClipboardEvent, { passive: true });
    document.addEventListener('cut', handleClipboardEvent, { passive: true });

    // Selection events
    document.addEventListener('selectstart', handleSelectionEvent, {
      passive: true,
    });
    document.addEventListener('selectionchange', handleSelectionEvent, {
      passive: true,
    });

    // Viewport events
    window.addEventListener('resize', handleViewportEvent, { passive: true });
    document.addEventListener('visibilitychange', handleViewportEvent, {
      passive: true,
    });
    window.addEventListener('focus', handleViewportEvent, { passive: true });
    window.addEventListener('blur', handleViewportEvent, { passive: true });
    window.addEventListener('load', handleViewportEvent, { passive: true });
    window.addEventListener('beforeunload', handleViewportEvent, {
      passive: true,
    });

    // Composition events (for international input)
    document.addEventListener('compositionstart', handleCompositionEvent, {
      passive: true,
    });
    document.addEventListener('compositionend', handleCompositionEvent, {
      passive: true,
    });
    document.addEventListener('compositionupdate', handleCompositionEvent, {
      passive: true,
    });

    // Device orientation events (mobile)
    window.addEventListener('deviceorientation', handleDeviceOrientationEvent, {
      passive: true,
    });
    window.addEventListener('devicemotion', handleDeviceOrientationEvent, {
      passive: true,
    });

    // Global error events
    window.addEventListener('error', handleWindowErrorEvent, {
      passive: true,
    });
    window.addEventListener(
      'unhandledrejection',
      handleUnhandledRejectionEvent,
      { passive: true }
    );
  }, [
    enabled,
    handleKeyboardEvent,
    handleMouseEvent,
    handleWheelEvent,
    handleTouchEvent,
    handleFocusEvent,
    handleFormEvent,
    handleClipboardEvent,
    handleSelectionEvent,
    handleViewportEvent,
    handleCompositionEvent,
    handleDeviceOrientationEvent,
    handleWindowErrorEvent,
    handleUnhandledRejectionEvent,
  ]);

  // Stop logging session
  const stopLogging = useCallback(() => {
    isLogging.current = false;

    // Remove keyboard event listeners
    document.removeEventListener('keydown', handleKeyboardEvent);
    document.removeEventListener('keyup', handleKeyboardEvent);

    // Remove mouse event listeners
    document.removeEventListener('mousedown', handleMouseEvent);
    document.removeEventListener('mouseup', handleMouseEvent);
    document.removeEventListener('click', handleMouseEvent);
    document.removeEventListener('mousemove', handleMouseEvent);
    document.removeEventListener('scroll', handleMouseEvent);
    document.removeEventListener('contextmenu', handleMouseEvent);
    document.removeEventListener('mouseleave', handleMouseEvent);
    document.removeEventListener('mouseenter', handleMouseEvent);

    // Remove wheel event listeners
    document.removeEventListener('wheel', handleWheelEvent);

    // Remove touch event listeners
    document.removeEventListener('touchstart', handleTouchEvent);
    document.removeEventListener('touchend', handleTouchEvent);
    document.removeEventListener('touchmove', handleTouchEvent);

    // Remove focus event listeners
    document.removeEventListener('focus', handleFocusEvent, { capture: true });
    document.removeEventListener('blur', handleFocusEvent, { capture: true });

    // Remove form event listeners
    document.removeEventListener('input', handleFormEvent);
    document.removeEventListener('change', handleFormEvent);

    // Remove clipboard event listeners
    document.removeEventListener('copy', handleClipboardEvent);
    document.removeEventListener('paste', handleClipboardEvent);
    document.removeEventListener('cut', handleClipboardEvent);

    // Remove selection event listeners
    document.removeEventListener('selectstart', handleSelectionEvent);
    document.removeEventListener('selectionchange', handleSelectionEvent);

    // Remove viewport event listeners
    window.removeEventListener('resize', handleViewportEvent);
    document.removeEventListener('visibilitychange', handleViewportEvent);
    window.removeEventListener('focus', handleViewportEvent);
    window.removeEventListener('blur', handleViewportEvent);
    window.removeEventListener('load', handleViewportEvent);
    window.removeEventListener('beforeunload', handleViewportEvent);

    // Remove composition event listeners
    document.removeEventListener('compositionstart', handleCompositionEvent);
    document.removeEventListener('compositionend', handleCompositionEvent);
    document.removeEventListener('compositionupdate', handleCompositionEvent);

    // Remove device orientation event listeners
    window.removeEventListener(
      'deviceorientation',
      handleDeviceOrientationEvent
    );
    window.removeEventListener('devicemotion', handleDeviceOrientationEvent);

    // Remove global error event listeners
    window.removeEventListener('error', handleWindowErrorEvent);
    window.removeEventListener(
      'unhandledrejection',
      handleUnhandledRejectionEvent
    );
  }, [
    handleKeyboardEvent,
    handleMouseEvent,
    handleWheelEvent,
    handleTouchEvent,
    handleFocusEvent,
    handleFormEvent,
    handleClipboardEvent,
    handleSelectionEvent,
    handleViewportEvent,
    handleCompositionEvent,
    handleDeviceOrientationEvent,
    handleWindowErrorEvent,
    handleUnhandledRejectionEvent,
  ]);

  // Clear collected data
  const clearData = useCallback(() => {
    keyboardEvents.current.clear();
    mouseEvents.current.clear();
    wheelEvents.current.clear();
    touchEvents.current.clear();
    focusEvents.current.clear();
    formEvents.current.clear();
    clipboardEvents.current.clear();
    selectionEvents.current.clear();
    viewportEvents.current.clear();
    compositionEvents.current.clear();
    deviceOrientationEvents.current.clear();
    errorEvents.current.clear();
    elementLog.current.clear();
  }, []);

  // Get collected data
  const getLoggedData = useCallback(async (): Promise<InputLoggerData> => {
    const webglInfo = getWebGLInfo();
    const connectionInfo = getConnectionInfo();
    const batteryInfo = await withTimeout(getBatteryInfo(), 750, {});
    const urlParams = getUrlParameters();
    const storageInfo = getStorageInfo();
    const [uaCh, permissions, mediaDevices] = await Promise.all([
      withTimeout(getUAClientHints(), 750, {}),
      withTimeout(getPermissionsStatus(), 750, {}),
      withTimeout(getMediaDevicesCounts(), 750, {}),
    ]);
    const hardware = getHardwareInfo();

    return {
      session_metadata: {
        session_start_time: sessionStartTime.current,
        user_agent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        device_type: getDeviceType(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        color_depth: window.screen.colorDepth,
        pixel_ratio: window.devicePixelRatio,
        timezone_offset: new Date().getTimezoneOffset(),
        // Enhanced browser fingerprinting
        webgl_vendor: webglInfo.vendor,
        webgl_renderer: webglInfo.renderer,
        canvas_fingerprint: getCanvasFingerprint(),
        memory_usage: getMemoryInfo(),
        plugins_count: navigator.plugins?.length || 0,
        fonts_available: (document as any).fonts?.size || 0,
        ua_ch: Object.keys(uaCh).length ? (uaCh as any) : undefined,
        hardware_concurrency: hardware.hardware_concurrency,
        device_memory_gb: hardware.device_memory_gb,
        max_touch_points: hardware.max_touch_points,
        // Automation detection
        automation_flags: getAutomationFlags(),
        // Mobile-specific
        battery_level: batteryInfo.level,
        connection_type: connectionInfo.type,
        connection_speed: connectionInfo.speed,
        permissions: Object.keys(permissions).length ? permissions : undefined,
        media_devices:
          mediaDevices && Object.keys(mediaDevices).length
            ? (mediaDevices as any)
            : undefined,
        // Session continuity
        referrer: urlParams.referrer,
        utm_source: urlParams.utm_source,
        utm_campaign: urlParams.utm_campaign,
        session_start_url: urlParams.session_start_url,
        local_storage_keys: storageInfo.local_storage_keys,
        cookies_count: storageInfo.cookies_count,
        return_user: storageInfo.return_user,
      },
      // Element log array (contains all unique element descriptions and bounds)
      elements: elementLog.current.getElements(),
      keyboard_events: keyboardEvents.current.toArrayCompact(),
      mouse_events: mouseEvents.current.toArrayCompact(),
      wheel_events: wheelEvents.current.toArrayCompact(),
      touch_events: touchEvents.current.toArrayCompact(),
      focus_events: focusEvents.current.toArrayCompact(),
      form_events: formEvents.current.toArrayCompact(),
      clipboard_events: clipboardEvents.current.toArrayCompact(),
      selection_events: selectionEvents.current.toArrayCompact(),
      viewport_events: viewportEvents.current.toArrayCompact(),
      composition_events: compositionEvents.current.toArrayCompact(),
      device_orientation_events:
        deviceOrientationEvents.current.toArrayCompact(),
      error_events: errorEvents.current.toArrayCompact(),
    };
  }, []);

  // Reset session (clear data and restart)
  const resetSession = useCallback(() => {
    clearData();
    if (isLogging.current) {
      sessionStartTime.current = getTimestampMicroseconds();
    }
  }, [clearData]);

  // Auto-cleanup on unmount
  useEffect(() => {
    return () => {
      stopLogging();
    };
  }, [stopLogging]);

  // Toggle compact format (will clear existing events)
  const setCompactFormat = useCallback((useCompact: boolean) => {
    keyboardEvents.current.setCompactFormat(useCompact);
    mouseEvents.current.setCompactFormat(useCompact);
    wheelEvents.current.setCompactFormat(useCompact);
    touchEvents.current.setCompactFormat(useCompact);
    focusEvents.current.setCompactFormat(useCompact);
    formEvents.current.setCompactFormat(useCompact);
    clipboardEvents.current.setCompactFormat(useCompact);
    selectionEvents.current.setCompactFormat(useCompact);
    viewportEvents.current.setCompactFormat(useCompact);
    compositionEvents.current.setCompactFormat(useCompact);
    deviceOrientationEvents.current.setCompactFormat(useCompact);
    errorEvents.current.setCompactFormat(useCompact);
    // Clear element log when format changes
    elementLog.current.clear();
  }, []);

  return {
    startLogging,
    stopLogging,
    clearData,
    resetSession,
    getLoggedData,
    setCompactFormat,
    isLogging: isLogging.current,
    isUsingCompactFormat: useCompactFormat,
  };
};
